import type { Popover } from '@base-ui/react/popover';
import { type CSSProperties, createElement, type ReactElement, type RefCallback, useCallback, useState } from 'react';

// Overrides user-agent popover styles
const TOP_LAYER_PORTAL_STYLE: CSSProperties = {
  display: 'block',
  margin: 0,
  right: 'auto',
  bottom: 'auto',
  border: 0,
  padding: 0,
  overflow: 'visible',
  background: 'transparent',
};

interface TopLayerPortalProps {
  portalAnchor: ReactElement;
  portalProps: {
    container: Popover.Portal.Props['container'];
    ref: RefCallback<HTMLDivElement>;
    popover: 'manual';
    style: CSSProperties;
  };
  positionerProps: {
    positionMethod: NonNullable<Popover.Positioner.Props['positionMethod']>;
    collisionBoundary: Popover.Positioner.Props['collisionBoundary'];
  };
}

/**
 * Allows a Base UI overlay render in the native top layer so it paints above a modal `<dialog>`.
 *
 * Portaling into the in-place `anchor` keeps the popup a DOM descendant of the dialog.
 * `showPopover()` lifts it into the top layer and escapes overflow clipping.
 */
export const useTopLayerPortal = (): TopLayerPortalProps => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const promoteToTopLayer = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      try {
        node.showPopover();
      } catch {
        // Disconnected or already open - both fine
      }
    }
  }, []);

  return {
    portalAnchor: createElement('span', { ref: setAnchor, className: 'contents' }),
    portalProps: {
      container: anchor,
      ref: promoteToTopLayer,
      popover: 'manual',
      style: TOP_LAYER_PORTAL_STYLE,
    },
    positionerProps: {
      positionMethod: 'fixed',
      // Must constrain to the viewport, since the popup paints in the top layer
      collisionBoundary: {
        x: 0,
        y: 0,
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      },
    },
  };
};
